import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck, Loader2, Sparkles, Lock } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

/**
 * DhfShareLandingPage — the receiving doctor's onboarding gate.
 *
 * Route: /dhf-share/:share_token
 *
 * Flow:
 *   1. Fetch landing metadata (referring doctor, patient first name, expiry).
 *   2. Doctor enters mobile → OTP delivered via MSG91 (only cost on this
 *      flow; not paid by DocSite).
 *   3. On verify: server matches phone_hash → existing doctor logs in,
 *      OR a new draft doctor account is provisioned. JWT + read-only
 *      DHF token returned.
 *   4. Existing doctor → straight to the shared DHF viewer.
 *      New doctor → the DocSite onboarding flow with the founder pitch:
 *        "Claim world's first fully encrypted #ClinicalPracticePlatform
 *         with your AI powered #YourOwnWebSite."
 */
const NAVY = '#16335b';

export default function DhfShareLandingPage() {
  const { share_token } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [metaErr, setMetaErr] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // phone | otp | done
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/dhf-share/${share_token}`);
        if (live) setMeta(data);
      } catch (e) {
        if (live) setMetaErr(formatApiError(e.response?.data?.detail) || 'This share link is invalid or has expired.');
      }
    })();
    return () => { live = false; };
  }, [share_token]);

  const sendOtp = async () => {
    setErr(''); setBusy(true);
    try {
      const clean = phone.replace(/\D/g, '');
      if (clean.length < 10) { setErr('Please enter a valid 10-digit mobile.'); return; }
      const { data } = await api.post(`/public/dhf-share/${share_token}/send-otp`, { phone: clean });
      setOtpToken(data.otp_token);
      setStep('otp');
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Could not send OTP. Please retry.');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setErr(''); setBusy(true);
    try {
      const clean = phone.replace(/\D/g, '');
      const { data } = await api.post(`/public/dhf-share/${share_token}/verify-otp`, {
        otp_token: otpToken,
        code: otp.trim(),
        name: (name || '').trim(),
        phone: clean,
      });
      // Persist auth (matches the AuthContext contract)
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.dhf_view_token) {
        // Straight to the shared DHF for existing OR new doctors.
        // New doctors will see an onboarding banner inside the dashboard.
        navigate(`/h/${data.dhf_view_token}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Verification failed. Please retry.');
    } finally { setBusy(false); }
  };

  if (metaErr) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mb-3">
            <Lock size={22} className="text-rose-500" />
          </div>
          <h1 className="text-lg font-bold text-slate-900">Link unavailable</h1>
          <p className="text-sm text-slate-600 mt-1">{metaErr}</p>
        </div>
      </div>
    );
  }

  if (!meta) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 size={22} className="animate-spin text-slate-400" /></div>;
  }

  return (
    <>
      <Helmet>
        <title>{`${meta.patient_first_name} shared a DHF · DocSite`}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen" style={{ background: 'linear-gradient(180deg,#f0f9ff 0%,#ffffff 40%)' }}>
        <div className="max-w-3xl mx-auto px-4 py-8 sm:py-14">
          {/* Referral card */}
          <div className="rounded-2xl bg-white border border-sky-100 shadow-sm p-5 sm:p-6" data-testid="dhf-share-referral-card">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wider">
                <ShieldCheck size={10} /> Zero-trust · Encrypted
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Referral</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {meta.patient_first_name} has shared their Digital Health File with you
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {meta.referring_doctor_name ? <>Powered by <b>{meta.referring_doctor_name}</b>{meta.referring_doctor_specialty ? ` · ${meta.referring_doctor_specialty}` : ''} · </> : null}
              Live in 5 minutes · ₹0 to start
            </p>

            {/* Mini-onboarding */}
            <div className="mt-5 space-y-3">
              {step === 'phone' && (
                <>
                  <label className="block">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Your full name</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dr. Priya Iyer"
                      className="w-full mt-1 rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="dhf-share-name" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Mobile</span>
                    <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile" inputMode="numeric" maxLength={10}
                      className="w-full mt-1 rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="dhf-share-phone" />
                  </label>
                  {err && <p className="text-[11px] text-rose-600">{err}</p>}
                  <button onClick={sendOtp} disabled={busy || !phone || phone.length < 10 || !name}
                    className="w-full py-3 rounded-xl text-white text-sm font-bold shadow hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
                    style={{ background: NAVY }} data-testid="dhf-share-send-otp">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={15} />}
                    {busy ? 'Sending OTP…' : 'Send OTP to open DHF'}
                  </button>
                </>
              )}

              {step === 'otp' && (
                <>
                  <p className="text-[12px] text-slate-600">OTP sent to +91 {phone}. Enter the 4 or 6-digit code.</p>
                  <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="OTP" inputMode="numeric" maxLength={6}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm tracking-widest text-center font-mono" data-testid="dhf-share-otp" />
                  {err && <p className="text-[11px] text-rose-600">{err}</p>}
                  <button onClick={verify} disabled={busy || otp.length < 4}
                    className="w-full py-3 rounded-xl text-white text-sm font-bold shadow hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
                    style={{ background: NAVY }} data-testid="dhf-share-verify">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                    {busy ? 'Verifying…' : 'Verify & open DHF'}
                  </button>
                  <button onClick={() => { setStep('phone'); setOtp(''); setErr(''); }} className="text-[11px] text-slate-500 hover:text-slate-700 mx-auto block">
                    Change number
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Founder pitch — same tone as the platform's launch copy */}
          <div className="mt-6 rounded-2xl bg-slate-900 text-white p-5 sm:p-6" data-testid="dhf-share-pitch">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300">Also unlocking for you</p>
            <h2 className="text-lg sm:text-xl font-bold mt-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Claim world&apos;s first fully encrypted #ClinicalPracticePlatform with your AI powered #YourOwnWebSite
            </h2>
            <ul className="mt-3 space-y-1.5 text-[13px] text-slate-300">
              <li>· AI-drafted, SEO-first website live in 5 minutes</li>
              <li>· Zero-trust encrypted Digital Health Files for every patient</li>
              <li>· Zero middlemen. Zero ad spend. Daily leads promise.</li>
            </ul>
            <p className="text-[11px] text-slate-400 mt-3 italic">
              You&apos;ll be signed up automatically after OTP verification — no extra form fill.
            </p>
          </div>

          <p className="text-[10px] text-slate-400 text-center mt-6">
            The patient&apos;s file will unlock read-only in your account. It never auto-fills your own composer.
          </p>
        </div>
      </div>
    </>
  );
}
